/**
 * Pagina PUBBLICA di prenotazione /meet/<token>: chi riceve la proposta di orari
 * sceglie uno slot e conferma — senza bisogno di un account Blagomail.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CalendarClock, Check, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { meetingsApi, type PublicMeetingProposal, type MeetingSlot } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

const fmtDay = (iso: string, tz: string) => {
  const s = new Intl.DateTimeFormat('it-IT', { timeZone: tz, weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso));
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const fmtTime = (iso: string, tz: string) =>
  new Intl.DateTimeFormat('it-IT', { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
/** Etichetta timezone corretta anche col cambio ora (es. "Roma (GMT+2)"). */
const tzLabel = (tz: string, at: Date) => {
  const off = new Intl.DateTimeFormat('it-IT', { timeZone: tz, timeZoneName: 'shortOffset' })
    .formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? '';
  const city = tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;
  return `${city}${off ? ` (${off})` : ''}`;
};

export function MeetPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [proposal, setProposal] = useState<PublicMeetingProposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState<{ slot: MeetingSlot; emailSent: boolean } | null>(null);

  useEffect(() => {
    if (!token) return;
    meetingsApi.getPublic(token)
      .then(({ proposal: p }) => {
        setProposal(p);
        const pre = parseInt(searchParams.get('slot') ?? '', 10);
        if (Number.isInteger(pre) && pre >= 0 && pre < p.slots.length && Date.parse(p.slots[pre]!.start) > Date.now()) {
          setSelectedIdx(pre);
        }
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Proposta non trovata'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const byDay = useMemo(() => {
    if (!proposal) return [] as [string, { slot: MeetingSlot; idx: number }[]][];
    const m = new Map<string, { slot: MeetingSlot; idx: number }[]>();
    proposal.slots.forEach((slot, idx) => {
      const d = fmtDay(slot.start, proposal.timezone);
      if (!m.has(d)) m.set(d, []);
      m.get(d)!.push({ slot, idx });
    });
    return [...m.entries()];
  }, [proposal]);

  async function handleBook() {
    if (!token || selectedIdx === null) return;
    setBooking(true);
    try {
      const res = await meetingsApi.book(token, {
        slotIndex: selectedIdx,
        name: name.trim() || undefined,
        email: email.trim() || undefined,
      });
      setDone({ slot: res.slot, emailSent: res.confirmationEmailSent });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Prenotazione fallita');
    } finally {
      setBooking(false);
    }
  }

  const tz = proposal?.timezone ?? 'Europe/Rome';

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 text-white mb-5">
          <Mail className="h-5 w-5" />
          <span className="font-bold text-lg tracking-tight">Blagomail</span>
        </div>

        <div className="bg-background rounded-2xl shadow-2xl p-6">
          {!proposal && !error && (
            <div className="flex items-center justify-center py-14 text-muted-foreground gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /> Carico la proposta…
            </div>
          )}

          {error && !done && (
            <div className="text-center py-10">
              <div className="text-3xl mb-3">😕</div>
              <p className="font-medium">{error}</p>
              <p className="text-sm text-muted-foreground mt-2">Contatta chi ti ha inviato la proposta per nuovi orari.</p>
            </div>
          )}

          {done && proposal && (
            <div className="text-center py-8">
              <div className="mx-auto h-14 w-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Check className="h-7 w-7 text-green-600" />
              </div>
              <h1 className="text-xl font-bold">Appuntamento confermato!</h1>
              <p className="mt-3 text-sm text-muted-foreground">{proposal.title} con {proposal.organizer_name}</p>
              <p className="mt-1 font-semibold">
                {fmtDay(done.slot.start, tz)} · {fmtTime(done.slot.start, tz)}–{fmtTime(done.slot.end, tz)}
              </p>
              <p className="mt-4 text-xs text-muted-foreground">
                L'organizzatore ha ricevuto la conferma e l'evento è già nel suo calendario.
                {done.emailSent ? ' Ti abbiamo inviato i dettagli via email, con l’invito da aggiungere al calendario.' : ' Prendi nota di data e ora.'}
              </p>
            </div>
          )}

          {proposal && !done && !error && proposal.status === 'booked' && (
            <div className="text-center py-10">
              <div className="text-3xl mb-3">📅</div>
              <p className="font-medium">Questo appuntamento è già stato confermato.</p>
              {proposal.booked_slot && (
                <p className="text-sm text-muted-foreground mt-2">
                  {fmtDay(proposal.booked_slot.start, tz)} · {fmtTime(proposal.booked_slot.start, tz)}
                </p>
              )}
            </div>
          )}

          {proposal && !done && !error && proposal.status === 'pending' && (
            <>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <CalendarClock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h1 className="font-bold text-lg leading-tight">{proposal.title}</h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    con {proposal.organizer_name} · {proposal.duration_minutes} min · {tzLabel(tz, new Date(proposal.slots[0]?.start ?? Date.now()))}
                  </p>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                {byDay.map(([day, items]) => (
                  <div key={day}>
                    <div className="text-xs font-semibold text-muted-foreground mb-2">{day}</div>
                    <div className="grid grid-cols-2 gap-2">
                      {items.map(({ slot, idx }) => {
                        const past = Date.parse(slot.start) < Date.now();
                        return (
                          <button
                            key={idx}
                            type="button"
                            disabled={past}
                            onClick={() => setSelectedIdx(idx)}
                            className={cn(
                              'h-11 rounded-lg border text-sm font-semibold transition-colors',
                              past && 'opacity-40 cursor-not-allowed',
                              selectedIdx === idx
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'border-input hover:border-primary hover:text-primary'
                            )}
                          >
                            {fmtTime(slot.start, tz)}–{fmtTime(slot.end, tz)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 space-y-3 border-t pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="bk-name" className="text-xs">Il tuo nome</Label>
                    <Input id="bk-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Mario Rossi" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="bk-email" className="text-xs">La tua email</Label>
                    <Input id="bk-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@azienda.it" />
                  </div>
                </div>
                <Button className="w-full h-11" disabled={selectedIdx === null || booking} onClick={() => void handleBook()}>
                  {booking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  {selectedIdx !== null
                    ? `Conferma ${fmtTime(proposal.slots[selectedIdx]!.start, tz)} di ${fmtDay(proposal.slots[selectedIdx]!.start, tz).split(' ')[0]?.toLowerCase()}`
                    : 'Scegli un orario'}
                </Button>
                <p className="text-[11px] text-center text-muted-foreground">
                  Alla conferma l'appuntamento entra nel calendario di {proposal.organizer_name}.
                </p>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-white/70 text-xs mt-4">
          Inviato con Blagomail — la casella email con AI di Blagoweb
        </p>
      </div>
    </div>
  );
}
